import { useState, useEffect, useRef, useCallback } from 'react';
import gameDesignService from '../../../utils/apiRequests/gameDesignService';

const SAVE_DEBOUNCE_MS = 800;

/**
 * Loads the project's game design document and exposes debounced
 * section-level save helpers.
 */
const useGameDesign = (projectId) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  const pendingRef = useRef({});
  const timeoutRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!projectId) return;
      setLoading(true);
      const response = await gameDesignService.getGameDesign(projectId);
      if (cancelled) return;
      if (!response.error) {
        setData(response.data?.data || response.data);
      }
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const flush = useCallback(async () => {
    const updates = pendingRef.current;
    pendingRef.current = {};
    if (Object.keys(updates).length === 0) return;
    setSaveState('saving');
    const response = await gameDesignService.updateGameDesign(projectId, updates);
    if (response.error) {
      setSaveState('error');
    } else {
      setSaveState('saved');
    }
  }, [projectId]);

  const updateSection = useCallback(
    (section, value) => {
      setData((prev) => ({ ...(prev || {}), [section]: value }));
      pendingRef.current = { ...pendingRef.current, [section]: value };
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        flush();
      }, SAVE_DEBOUNCE_MS);
    },
    [flush]
  );

  // Flush pending changes on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        flush();
      }
    };
  }, [flush]);

  return { data, loading, saveState, updateSection };
};

export default useGameDesign;
